/*
 * Carrito en móvil — Mejoras UX Rivera (punto 4, septiembre 2026)
 *
 * 1. Envoltorio de regalo plegado (snippets/cross-sells.liquid, ajuste rivera_cross_sells_collapsed).
 *    - El título es un botón que abre y cierra la lista; el otro carrito (cajón o página) sigue igual.
 *    - Si la compradora lo abre, sigue abierto durante la sesión de la pestaña (sessionStorage). Stiletto
 *      repinta los carritos con innerHTML y el Liquid lo pinta plegado: un MutationObserver lo reabre
 *      antes de pintar. La carga inicial la resuelve el <script> en línea del snippet.
 * 2. Barra fija de pago en /cart (snippets/carrito-barra-fija.liquid, ajuste rivera_cart_sticky_checkout).
 *    - Se ve por debajo de 960 px mientras el bloque de acciones del pie (pagar y pago acelerado) no
 *      asome por encima de ella, sin capas abiertas del tema y sin foco en un campo editable.
 *    - Copia el importe del pie cada vez que Stiletto lo repinta.
 *
 * Cada parte solo actúa si su marcado existe: con el ajuste apagado, el Liquid no lo pinta y este
 * archivo ni se carga (layout/theme.liquid).
 * Spec y plan: _SPECS/eduardo-rivera/004-carrito-movil.
 */
(() => {
  const SEL = {
    pagina: '[data-section-type="cart"]',
    cajon: '.quick-cart',
    regalo: '[data-cross-sells-toggle]',
    barra: '[data-barra-pago]',
    importe: '[data-barra-pago-importe]',
    importePie: '.cart__footer-subtotal span',
    acciones: '.cart__input-buttons, .additional-checkout-buttons',
    capasApiladas: '.quick-cart__wrapper.active, [data-quick-view-modal].active',
    editable: 'input:not([type="checkbox"]):not([type="radio"]):not([type="submit"]):not([type="button"]):not([type="hidden"]), textarea, select, [contenteditable="true"]',
  };
  const CLAVE_REGALO = 'rivera:regalo-abierto';
  const movil = window.matchMedia('(max-width: 959px)');

  // --- 1. Envoltorio plegado ---------------------------------------------------------------------
  const regaloGuardado = () => {
    try {
      return sessionStorage.getItem(CLAVE_REGALO) === '1';
    } catch (e) {
      return false;
    }
  };
  const guardarRegalo = abierto => {
    try {
      sessionStorage.setItem(CLAVE_REGALO, abierto ? '1' : '0');
    } catch (e) {
      // Sin almacenamiento: el estado dura lo que dure la página.
    }
  };

  function pintarRegalo(boton, abierto) {
    const panel = document.getElementById(boton.getAttribute('aria-controls'));
    boton.setAttribute('aria-expanded', String(abierto));
    if (!panel || panel.hidden === !abierto) return;
    panel.hidden = !abierto;
    // El carrusel del cajón (Swiper) se montó con la lista oculta: que vuelva a medir.
    if (abierto && panel.classList.contains('swiper')) window.dispatchEvent(new Event('resize'));
  }

  function reabrirRegalo() {
    if (!regaloGuardado()) return;
    document.querySelectorAll(`${SEL.regalo}[aria-expanded="false"]`).forEach(boton => pintarRegalo(boton, true));
  }

  document.addEventListener('click', e => {
    const boton = e.target.closest(SEL.regalo);
    if (!boton) return;
    const abierto = boton.getAttribute('aria-expanded') !== 'true';
    guardarRegalo(abierto);
    document.querySelectorAll(SEL.regalo).forEach(b => pintarRegalo(b, abierto));
  });

  // --- 2. Barra fija de pago ---------------------------------------------------------------------
  const barraEstado = { barra: null, observador: null, medida: false, accionesVisibles: false, foco: false };
  // Stiletto marca todas sus capas con un solo atributo del body. Al cerrar una capa que estaba encima de
  // otra (la vista rápida sobre el carrito lateral), lo pone a "false" aunque la de debajo siga abierta
  // (theme.js:7173), así que también se miran las dos capas que se apilan.
  const capaAbierta = () =>
    document.body.getAttribute('data-fluorescent-overlay-open') === 'true' || Boolean(document.querySelector(SEL.capasApiladas));

  function pintarBarra() {
    const { barra } = barraEstado;
    if (!barra) return;
    // Hasta la primera medida del IntersectionObserver no se sabe si las acciones asoman: oculta.
    const ver = barraEstado.medida && movil.matches && !barraEstado.accionesVisibles && !barraEstado.foco && !capaAbierta();
    barra.classList.toggle('is-visible', ver);
    if (ver) barra.removeAttribute('inert');
    else barra.setAttribute('inert', '');
  }

  function copiarImporte() {
    const { barra } = barraEstado;
    if (!barra) return;
    const origen = document.querySelector(`${SEL.pagina} ${SEL.importePie}`);
    const destino = barra.querySelector(SEL.importe);
    if (!origen || !destino) return;
    const texto = origen.textContent.trim();
    if (destino.textContent.trim() !== texto) destino.textContent = texto;
  }

  function montarBarra(forzar) {
    const barra = document.querySelector(SEL.barra);
    if (barra === barraEstado.barra && !forzar) {
      copiarImporte();
      return;
    }
    if (barraEstado.observador) barraEstado.observador.disconnect();
    Object.assign(barraEstado, { barra, observador: null, medida: false, accionesVisibles: false });
    if (!barra) return;

    const alto = barra.offsetHeight;
    document.documentElement.style.setProperty('--barra-pago-alto', `${alto}px`);
    const zonas = document.querySelectorAll(SEL.acciones.split(',').map(s => `${SEL.pagina} ${s.trim()}`).join(','));
    const vistas = new Set();
    barraEstado.observador = new IntersectionObserver(
      entradas => {
        entradas.forEach(entrada => (entrada.isIntersecting ? vistas.add(entrada.target) : vistas.delete(entrada.target)));
        barraEstado.accionesVisibles = vistas.size > 0;
        barraEstado.medida = true;
        pintarBarra();
      },
      // «Asoma» es entrar en la pantalla por encima de la barra.
      { rootMargin: `0px 0px -${alto}px 0px` }
    );
    zonas.forEach(zona => barraEstado.observador.observe(zona));
    if (!zonas.length) barraEstado.medida = true;
    copiarImporte();
    pintarBarra();
  }

  // Foco en un campo editable (la cantidad): la barra se aparta para no tapar el teclado.
  const hayFoco = () => Boolean(document.activeElement && document.activeElement.matches && document.activeElement.matches(SEL.editable));
  const revisarFoco = () => {
    const foco = hayFoco();
    if (foco === barraEstado.foco) return;
    barraEstado.foco = foco;
    pintarBarra();
  };
  document.addEventListener('focusin', revisarFoco);
  document.addEventListener('focusout', () => setTimeout(revisarFoco, 0));

  // Capas del tema (cajón, vista rápida, menú, modales): la barra se oculta mientras hay una abierta.
  new MutationObserver(pintarBarra).observe(document.body, { attributes: true, attributeFilter: ['data-fluorescent-overlay-open'] });

  // --- Repintados, cambios de tamaño y vuelta atrás ----------------------------------------------
  const observadas = new WeakSet();
  const alRepintar = () => {
    reabrirRegalo();
    montarBarra(false);
  };
  function observarSecciones() {
    document.querySelectorAll(`${SEL.pagina}, ${SEL.cajon}`).forEach(raiz => {
      if (observadas.has(raiz)) return;
      observadas.add(raiz);
      new MutationObserver(alRepintar).observe(raiz, { childList: true, subtree: true });
    });
  }

  const remontar = () => montarBarra(true);
  if (movil.addEventListener) movil.addEventListener('change', remontar);
  else if (movil.addListener) movil.addListener(remontar);
  let esperaResize;
  window.addEventListener('resize', () => {
    clearTimeout(esperaResize);
    esperaResize = setTimeout(remontar, 200);
  });
  window.addEventListener('pageshow', e => {
    if (!e.persisted) return;
    reabrirRegalo();
    remontar();
  });
  document.addEventListener('shopify:section:load', () => {
    observarSecciones();
    reabrirRegalo();
    remontar();
  });

  observarSecciones();
  reabrirRegalo();
  montarBarra(true);
})();
